import { request } from "./client";
import type { ManagerEvent } from "@/types/manager";

export type BookingJob = {
  id: string;
  status: string;
  total_artists?: number;
  processed_artists?: number;
  failed_artists?: number;
  succeeded_artists?: number;
  created_at?: string;
  updated_at?: string;
};

export type BookingLeadRecommendation = {
  id: number;
  job_id: string;
  lead_id: string;
  score: number;
  status: string;
  rationale?: string | null;
  components?: Record<string, number> | null;
  feature_contributions?: Record<string, Record<string, unknown>> | null;
  lead_metadata?: Record<string, unknown> | null;
  crm_status?: string | null;
  crm_result_id?: number | null;
  created_at: string;
  updated_at: string;
  confirmed_at?: string | null;
  sent_at?: string | null;
};

export type BookingEventPayload = {
  show_id: string;
  artist_id: string;
  artist_name?: string | null;
  event_date: string;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  venue?: string | null;
  supporting_acts: string[];
  source_urls: string[];
  last_updated: string;
};

export type ManualBookingEventInput = {
  artist_id?: string;
  artist_name?: string;
  event_name?: string;
  event_date: string;
  venue: string;
  city?: string;
  state?: string;
  country?: string;
  supporting_acts?: string[];
  ticket_url?: string;
  contact_name?: string;
  contact_email?: string;
  notes?: string;
  source_urls?: string[];
  metadata?: Record<string, unknown>;
  publish_upstream?: boolean;
  lead_id?: number;
  /** Booking opportunity this show completes; the server's idempotency and provenance key. */
  booking_lead_id?: number;
  guarantee_amount?: number | null;
  split_terms?: string | null;
  artist_percentage?: number | null;
  house_expense?: number | null;
  sound_expense?: number | null;
};

export type ManualBookingEventResponse = {
  event: BookingEventPayload;
  manager_event?: ManagerEvent;
  publish_result?: Record<string, unknown> | null;
  /** False when a retry returned the booking's existing show. */
  created?: boolean;
};

export type ManualBookingLeadInput = {
  contact_id?: number;
  crm_lead_id?: number;
  company_name?: string;
  contact_name?: string;
  contact_email?: string;
  phone?: string;
  city?: string;
  state?: string;
  region?: string;
  country?: string;
  tags?: string[];
  agent_owner?: string;
  pipeline_stage?: string;
  window_start?: string;
  window_end?: string;
  notes?: string;
  lead_id?: string;
  source?: string;
  metadata?: Record<string, unknown>;
  score?: number;
  status?: string;
  rationale?: string;
  components?: Record<string, number>;
  feature_contributions?: Record<string, Record<string, unknown>>;
};

type BookingLeadQuery = {
  status?: string;
  limit?: number;
  jobId?: string;
};

export type BookingLeadActionResponse = BookingLeadRecommendation & {
  crm_result?: unknown;
};

export function createIngestionJob(
  artistIds: string[] = [],
  { includePast = false }: { includePast?: boolean } = {},
) {
  return request<BookingJob>("/booking/jobs", {
    method: "POST",
    body: { artist_ids: artistIds, include_past: includePast },
  });
}

export function getIngestionJobs(limit = 10) {
  return request<BookingJob[]>(`/booking/jobs?limit=${encodeURIComponent(limit)}`).then((payload) =>
    Array.isArray(payload) ? payload : [],
  );
}

export function getIngestionHistory(limit = 25, offset = 0) {
  const params = new URLSearchParams();
  if (typeof limit === "number") params.set("limit", String(limit));
  if (typeof offset === "number" && offset > 0) params.set("offset", String(offset));
  const suffix = params.toString();
  return request(`/booking/history${suffix ? `?${suffix}` : ""}`).then((payload) =>
    Array.isArray(payload) ? payload : [],
  );
}

export function createManualBookingEvent(body: ManualBookingEventInput) {
  return request<ManualBookingEventResponse>("/booking/events", {
    method: "POST",
    body,
  });
}

export function createManualBookingLead(body: ManualBookingLeadInput) {
  return request<BookingLeadRecommendation>("/booking/leads/manual", {
    method: "POST",
    body,
  });
}

export function getIngestionHealth() {
  return request("/booking/health");
}

export function getCrmResults(limit = 25) {
  return request(`/booking/crm/results?limit=${encodeURIComponent(limit)}`).then((payload) =>
    Array.isArray(payload) ? payload : [],
  );
}

export function syncLeadToCrm(body: Record<string, unknown>) {
  return request("/booking/crm/sync", {
    method: "POST",
    body,
  });
}

export function getBookingLeads({ status, limit = 50, jobId }: BookingLeadQuery = {}) {
  const params = new URLSearchParams();
  if (typeof limit === "number") params.set("limit", String(limit));
  if (status) params.set("status", status);
  if (jobId) params.set("job_id", jobId);
  const query = params.toString();
  const suffix = query ? `?${query}` : "";
  return request<BookingLeadRecommendation[]>(`/booking/leads${suffix}`).then((payload) =>
    Array.isArray(payload) ? payload : [],
  );
}

export function confirmBookingLead(id: number | string) {
  return request<BookingLeadRecommendation>(`/booking/leads/${encodeURIComponent(id)}/confirm`, { method: "POST" });
}

export function denyBookingLead(id: number | string) {
  return request<BookingLeadRecommendation>(`/booking/leads/${encodeURIComponent(id)}/deny`, { method: "POST" });
}

export function sendBookingLead(id: number | string) {
  return request<{
    recommendation: BookingLeadRecommendation;
    crm_result?: unknown;
  }>(`/booking/leads/${encodeURIComponent(id)}/send`, { method: "POST" });
}

export function getLeadRecommendations(limit = 20) {
  return request(`/leads/recommendations?limit=${encodeURIComponent(limit)}`).then((payload) =>
    Array.isArray(payload) ? payload : [],
  );
}

export function updateBookingLeadStage(id: number | string, stage: string) {
  return request(`/booking/leads/${encodeURIComponent(id)}/stage`, {
    method: "POST",
    body: { stage },
  });
}

export function updateBookingLeadWindow(
  id: number | string,
  windowStart?: string,
  windowEnd?: string,
) {
  return request<BookingLeadRecommendation>(`/booking/leads/${encodeURIComponent(id)}/window`, {
    method: "POST",
    body: {
      window_start: windowStart,
      window_end: windowEnd,
    },
  });
}
